// Moving a file's bytes, which never pass through the server: the API signs
// a URL for one file, and the browser talks to the bucket with it.

import { unwrap } from '../api/client';
import { signDownload, signUpload } from '../api/gen';

/**
 * Uploads one file to key and reports progress from 0 to 1. XMLHttpRequest
 * rather than fetch, because fetch cannot report upload progress. A single
 * PUT: multipart upload for very large files is later work (concept
 * section 6).
 */
export async function upload(key: string, file: File, onProgress: (fraction: number) => void): Promise<void> {
  const { url } = await unwrap(signUpload({ body: { key } }));
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (file.type) xhr.setRequestHeader('Content-Type', file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`the bucket refused the upload (HTTP ${xhr.status})`));
    xhr.onerror = () => reject(new Error('the bucket could not be reached; is its CORS open to this origin?'));
    xhr.send(file);
  });
  onProgress(1);
}

/** A URL to show the file in the page: a video, an image or a PDF. */
export async function previewURL(key: string): Promise<string> {
  return (await unwrap(signDownload({ body: { key, inline: true } }))).url;
}

/** Saves the file: the bucket answers with an attachment. */
export async function download(key: string): Promise<void> {
  const { url } = await unwrap(signDownload({ body: { key, inline: false } }));
  const a = document.createElement('a');
  a.href = url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
